// Pure cobweb layout computation extracted 1:1 from RoomCanvas.tsx.
// Maps DB cobweb rows → deterministic on-screen positions (floor tile or furniture
// surface) using a per-house seeded RNG. No PIXI, no React — plain args in, array out.
import type { CobwebResponse } from '../../client';
import type { RenderFurniture } from '../../domain/types';
import { TILE_WIDTH, TILE_HEIGHT, MAP_WIDTH_TILES } from '../../domain/windowSnap';
import { isWallMountedItem, isRugCategory } from '../../domain/tileMath';
import { toPlacementMode, isSurface } from './interaction/placement';

// Discriminated union for cobweb entries (floor tile vs furniture surface)
export type CobwebItem =
  | { id: number; size: number; kind: 'floor'; tileX: number; tileY: number; }
  | { id: number; size: number; kind: 'surface'; furnitureId: number; surfaceTileX: number; surfaceTileY: number; };

// `position_index` from the DB replaces the old loop variable `i`, so positions
// are deterministic per house and stable across deletion (removed rows are gone from DB).
export function computeCobwebLayout(
  cobwebsData: CobwebResponse[],
  mapObject: { type: string } | undefined,
  enrichedHouseFurniture: RenderFurniture[],
  mapObjectId: number,
): CobwebItem[] {
  if (!cobwebsData.length) return [];
  if (!mapObject || mapObject.type !== 'house') return [];

  const halfTW = TILE_WIDTH / 2;
  const halfTH = TILE_HEIGHT / 2;

  // Collect surface positions and providers, and occupied floor tiles from furniture
  const surfaceItemPositions: Array<{ x: number; y: number }> = [];
  type SurfaceProvider = { furnitureId: number; screenX: number; screenY: number; tiles: Array<{ tx: number; ty: number }> };
  const surfaceProviders: SurfaceProvider[] = [];
  const occupiedFloor = new Set<string>();

  for (const f of enrichedHouseFurniture) {
    if (!f.is_placed || f.x === null || f.y === null) continue;
    const cat = f.furniture_catalog;
    if (!cat) continue;
    const isOnSurface = isSurface(toPlacementMode(f.placement_mode));

    if (isOnSurface) {
      surfaceItemPositions.push({ x: f.x ?? 0, y: f.y ?? 0 });
      continue;
    }
    if (isWallMountedItem(cat) || isRugCategory(cat.category)) continue;

    const centerTileX = Math.round(((f.x ?? 0) / halfTW + (f.y ?? 0) / halfTH) / 2);
    const centerTileY = Math.round(((f.y ?? 0) / halfTH - (f.x ?? 0) / halfTW) / 2);

    const fpTiles = cat.footprint_tiles;
    if (fpTiles && fpTiles.length > 0) {
      for (const [dx, dy] of fpTiles) {
        const [tdx, tdy] = f.is_mirrored ? [dy, dx] : [dx, dy];
        occupiedFloor.add(`${centerTileX + tdx},${centerTileY + tdy}`);
      }
    } else {
      for (let tx = -1; tx <= 1; tx++) {
        for (let ty = -1; ty <= 1; ty++) {
          occupiedFloor.add(`${centerTileX + tx},${centerTileY + ty}`);
        }
      }
    }

    // FurnitureCatalog.surface_tiles is Array<Array<number>>; pairs are [dx, dy].
    const surfTiles = cat.surface_tiles as [number, number][] | null | undefined;
    if (surfTiles && surfTiles.length > 0) {
      const absTiles = surfTiles.map(([dx, dy]) => {
        const [tdx, tdy] = f.is_mirrored ? [dy, dx] : [dx, dy];
        return { tx: centerTileX + tdx, ty: centerTileY + tdy };
      });
      surfaceProviders.push({ furnitureId: f.id, screenX: f.x ?? 0, screenY: f.y ?? 0, tiles: absTiles });
    }
  }

  const PROX_X = 3 * TILE_WIDTH;
  const PROX_Y = 8 * TILE_HEIGHT;
  const freeSurfaceProviders = surfaceProviders.filter(
    sp => !surfaceItemPositions.some(
      si => Math.abs(si.x - sp.screenX) < PROX_X && Math.abs(si.y - sp.screenY) < PROX_Y
    )
  );

  const seed = mapObjectId * 1337;
  const rng = (n: number) => { const x = Math.sin(seed + n) * 10000; return x - Math.floor(x); };

  const SPAWN_MARGIN = 5;
  const SPAWN_RANGE = MAP_WIDTH_TILES - 2 * SPAWN_MARGIN; // 30
  const result: CobwebItem[] = [];

  for (const cobweb of cobwebsData) {
    const i = cobweb.position_index; // use DB index as RNG seed offset (same formula as before)
    const size = 66 + rng(i * 3 + 2) * 66; // 66–132px

    // 40% chance to use a free surface if available
    if (freeSurfaceProviders.length > 0 && rng(i * 97 + 1) < 0.4) {
      const pIdx = Math.floor(rng(i * 53 + 3) * freeSurfaceProviders.length);
      const provider = freeSurfaceProviders[pIdx];
      const tIdx = Math.floor(rng(i * 71 + 5) * provider.tiles.length);
      const { tx, ty } = provider.tiles[tIdx];
      result.push({ id: cobweb.id, size, kind: 'surface', furnitureId: provider.furnitureId, surfaceTileX: tx, surfaceTileY: ty });
      continue;
    }

    // Floor tile: try 30 seeded positions to find a free, safe one.
    // MARGIN=5 keeps cobweb centers ≥5 tiles from each rectangular edge.
    // Diamond corner constraints prevent visual overflow near the 4 diamond vertices:
    //   tileX+tileY ≥ 12  → screenY ≥ 75 ≥ max-radius 66 (top vertex safe)
    //   tileX+tileY ≤ 68  → cobweb bottom ≤ 491 ≤ 500 (bottom vertex safe)
    //   |tileX-tileY| ≤ 28 → cobweb edge ≤ 416 ≤ 500 (left/right vertex safe)
    for (let attempt = 0; attempt < 30; attempt++) {
      const tileX = Math.floor(SPAWN_MARGIN + rng(i * 37 + attempt * 7) * SPAWN_RANGE);
      const tileY = Math.floor(SPAWN_MARGIN + rng(i * 37 + attempt * 7 + 1) * SPAWN_RANGE);
      const tileSum = tileX + tileY;
      const tileDiff = Math.abs(tileX - tileY);
      const isInsideFloor = tileSum >= 12 && tileSum <= 68 && tileDiff <= 28;
      if (isInsideFloor && !occupiedFloor.has(`${tileX},${tileY}`)) {
        result.push({ id: cobweb.id, size, kind: 'floor', tileX, tileY });
        break;
      }
    }
  }
  return result;
}
